import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Link2, Unlink, Facebook, Twitter, Linkedin, Instagram, Settings, Shield,
  Loader2, CheckCircle2, XCircle, AlertCircle, ExternalLink, Bell, Mail,
  TrendingDown, RefreshCw, Plus, Trash2, Clock
} from 'lucide-react';
import { socialMediaAPI, landingPageAPI } from '../api';
import { format } from 'date-fns';

const PLATFORMS = [
  { 
    id: 'facebook', 
    name: 'Facebook', 
    icon: Facebook, 
    color: 'bg-blue-600',
    description: 'Connect your Facebook Page to publish posts directly'
  },
  { 
    id: 'twitter', 
    name: 'Twitter / X', 
    icon: Twitter, 
    color: 'bg-black',
    description: 'Connect your Twitter/X account to publish tweets'
  },
  { 
    id: 'linkedin', 
    name: 'LinkedIn', 
    icon: Linkedin, 
    color: 'bg-blue-700',
    description: 'Connect your LinkedIn profile to publish updates'
  },
];

export const SocialMediaSettingsPage = () => {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [status, setStatus] = useState({ configured: {}, connected: {} });
  const [alerts, setAlerts] = useState([]);
  const [pages, setPages] = useState([]);
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [newAlert, setNewAlert] = useState({
    landing_page_id: 'all',
    alert_type: 'threshold_breach',
    threshold_value: 2.0,
    comparison_period_days: 7,
    email_recipients: []
  });
  const [emailInput, setEmailInput] = useState('');

  useEffect(() => {
    // Handle OAuth callback
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const platform = searchParams.get('platform');

    if (success && platform) {
      toast.success(`${platform.charAt(0).toUpperCase() + platform.slice(1)} connected successfully!`);
      navigate('/settings/social-media', { replace: true });
    }
    if (error && platform) {
      toast.error(`Failed to connect ${platform}: ${error}`);
      navigate('/settings/social-media', { replace: true });
    }

    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statusRes, alertsRes, pagesRes] = await Promise.all([
        socialMediaAPI.getStatus(),
        socialMediaAPI.getAlerts(),
        landingPageAPI.getAll({ limit: 100 })
      ]);
      setStatus(statusRes.data);
      setAlerts(alertsRes.data?.alerts || []);
      setPages(pagesRes.data?.pages || pagesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (platform) => {
    try {
      setConnecting(platform);
      const res = await socialMediaAPI.getAuthUrl(platform);
      if (res.data?.authorization_url) {
        window.location.href = res.data.authorization_url;
      } else {
        toast.error('Failed to get authorization URL');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to connect';
      toast.error(errorMsg);
      setConnecting(null);
    }
  };

  const handleDisconnect = async (platform) => {
    if (!confirm(`Are you sure you want to disconnect ${platform}?`)) return;
    
    try {
      await socialMediaAPI.disconnect(platform);
      toast.success(`${platform} disconnected`);
      fetchData();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleCreateAlert = async () => {
    try {
      await socialMediaAPI.createAlert({
        ...newAlert,
        landing_page_id: newAlert.landing_page_id === 'all' ? null : (newAlert.landing_page_id || null),
        email_recipients: newAlert.email_recipients
      });
      toast.success('Alert created');
      setShowAlertDialog(false);
      setNewAlert({
        landing_page_id: 'all',
        alert_type: 'threshold_breach',
        threshold_value: 2.0,
        comparison_period_days: 7,
        email_recipients: []
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create alert');
    }
  };

  const handleDeleteAlert = async (alertId) => {
    if (!confirm('Delete this alert?')) return;
    try {
      await socialMediaAPI.deleteAlert(alertId);
      toast.success('Alert deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  const handleToggleAlert = async (alert) => {
    try {
      await socialMediaAPI.updateAlert(alert.id, { is_active: !alert.is_active });
      toast.success(`Alert ${alert.is_active ? 'disabled' : 'enabled'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update alert');
    }
  };

  const addEmail = () => {
    if (emailInput && !newAlert.email_recipients.includes(emailInput)) {
      setNewAlert({
        ...newAlert,
        email_recipients: [...newAlert.email_recipients, emailInput]
      });
      setEmailInput('');
    }
  };

  const removeEmail = (email) => {
    setNewAlert({
      ...newAlert,
      email_recipients: newAlert.email_recipients.filter(e => e !== email)
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="social-media-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Social Media & Alerts
          </h1>
          <p className="text-muted-foreground">
            Connect social accounts and configure performance alerts
          </p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="connections">
        <TabsList>
          <TabsTrigger value="connections">Social Connections</TabsTrigger>
          <TabsTrigger value="alerts">Performance Alerts</TabsTrigger>
        </TabsList>

        {/* Social Connections Tab */}
        <TabsContent value="connections" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Link2 className="h-5 w-5" />
                Connected Accounts
              </CardTitle>
              <CardDescription>
                Connect your social media accounts to publish content directly from campaigns
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {PLATFORMS.map(platform => {
                const isConfigured = status.configured?.[platform.id];
                const connection = status.connected?.[platform.id];
                const isConnected = !!connection;
                const Icon = platform.icon;

                return (
                  <div 
                    key={platform.id}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      isConnected ? 'bg-green-50 border-green-200' : 'bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-lg ${platform.color} flex items-center justify-center`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          {platform.name}
                          {isConnected && (
                            <Badge variant="outline" className="bg-green-100 text-green-700">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Connected
                            </Badge>
                          )}
                        </p>
                        {isConnected ? (
                          <p className="text-sm text-muted-foreground">
                            @{connection.username || connection.name}
                            {connection.connected_at && (
                              <span className="ml-2">
                                • Connected {format(new Date(connection.connected_at), 'MMM d, yyyy')}
                              </span>
                            )}
                          </p>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            {isConfigured ? platform.description : 'OAuth credentials not configured'}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {isConnected ? (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDisconnect(platform.id)}
                        >
                          <Unlink className="h-4 w-4 mr-2" />
                          Disconnect
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleConnect(platform.id)}
                          disabled={!isConfigured || connecting === platform.id}
                          className={platform.color}
                        >
                          {connecting === platform.id ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Link2 className="h-4 w-4 mr-2" />
                          )}
                          Connect
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* OAuth Setup Instructions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5" />
                OAuth Configuration
              </CardTitle>
              <CardDescription>
                To enable social media publishing, add your OAuth credentials to the backend environment
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-100 p-4 rounded-lg font-mono text-sm space-y-2">
                <p className="text-muted-foreground"># Add to backend/.env</p>
                <p>FACEBOOK_CLIENT_ID=your_app_id</p>
                <p>FACEBOOK_CLIENT_SECRET=your_app_secret</p>
                <p>FACEBOOK_REDIRECT_URI=https://yourapp.com/api/social-media/oauth/callback/facebook</p>
                <p className="mt-2">TWITTER_CLIENT_ID=your_client_id</p>
                <p>TWITTER_CLIENT_SECRET=your_client_secret</p>
                <p>TWITTER_REDIRECT_URI=https://yourapp.com/api/social-media/oauth/callback/twitter</p>
                <p className="mt-2">LINKEDIN_CLIENT_ID=your_client_id</p>
                <p>LINKEDIN_CLIENT_SECRET=your_client_secret</p>
                <p>LINKEDIN_REDIRECT_URI=https://yourapp.com/api/social-media/oauth/callback/linkedin</p>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button variant="outline" size="sm" asChild>
                  <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Facebook Developer Portal
                  </a>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a href="https://developer.twitter.com/en/portal" target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Twitter Developer Portal
                  </a>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a href="https://www.linkedin.com/developers/apps" target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    LinkedIn Developer Portal
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Performance Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Landing Page Performance Alerts
                </CardTitle>
                <CardDescription>
                  Get notified when conversion rates drop or receive periodic summaries
                </CardDescription>
              </div>
              <Button onClick={() => setShowAlertDialog(true)} data-testid="create-alert-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Alert
              </Button>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <div className="text-center py-12">
                  <TrendingDown className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-2">No alerts configured</p>
                  <p className="text-sm text-muted-foreground">
                    Create alerts to monitor landing page performance
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.map(alert => {
                    const page = pages.find(p => p.id === alert.landing_page_id);
                    return (
                      <div 
                        key={alert.id}
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          alert.is_active ? 'bg-white' : 'bg-slate-50 opacity-60'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            alert.alert_type === 'threshold_breach' ? 'bg-red-100' : 'bg-blue-100'
                          }`}>
                            {alert.alert_type === 'threshold_breach' ? (
                              <TrendingDown className="h-5 w-5 text-red-600" />
                            ) : (
                              <Clock className="h-5 w-5 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">
                              {alert.alert_type === 'threshold_breach' 
                                ? `Conversion drops below ${alert.threshold_value}%`
                                : alert.alert_type === 'daily_summary'
                                  ? 'Daily Summary'
                                  : 'Weekly Summary'
                              }
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {alert.landing_page_id 
                                ? `Page: ${page?.name || 'Unknown'}`
                                : 'All landing pages'
                              }
                              {alert.email_recipients?.length > 0 && (
                                <span className="ml-2">• {alert.email_recipients.length} recipient(s)</span>
                              )}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={alert.is_active}
                            onCheckedChange={() => handleToggleAlert(alert)}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAlert(alert.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Alert Dialog */}
      <Dialog open={showAlertDialog} onOpenChange={setShowAlertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Performance Alert</DialogTitle>
            <DialogDescription>
              Configure alerts for landing page performance monitoring
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Alert Type</Label>
              <Select 
                value={newAlert.alert_type} 
                onValueChange={(v) => setNewAlert({ ...newAlert, alert_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="threshold_breach">Conversion Rate Threshold</SelectItem>
                  <SelectItem value="daily_summary">Daily Summary</SelectItem>
                  <SelectItem value="weekly_summary">Weekly Summary</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Landing Page</Label>
              <Select 
                value={newAlert.landing_page_id} 
                onValueChange={(v) => setNewAlert({ ...newAlert, landing_page_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All pages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Landing Pages</SelectItem>
                  {pages.map(page => (
                    <SelectItem key={page.id} value={page.id}>{page.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {newAlert.alert_type === 'threshold_breach' && (
              <div className="space-y-2">
                <Label>Threshold (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={newAlert.threshold_value}
                  onChange={(e) => setNewAlert({ ...newAlert, threshold_value: parseFloat(e.target.value) })}
                />
                <p className="text-xs text-muted-foreground">
                  Alert triggers when conversion rate falls below this value
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Email Recipients</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addEmail())}
                />
                <Button type="button" variant="outline" onClick={addEmail}>Add</Button>
              </div>
              {newAlert.email_recipients.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {newAlert.email_recipients.map(email => (
                    <Badge key={email} variant="secondary" className="pr-1">
                      {email}
                      <button 
                        onClick={() => removeEmail(email)}
                        className="ml-1 hover:text-red-500"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAlertDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateAlert}>Create Alert</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SocialMediaSettingsPage;
